import { v4 as uuid } from 'uuid';
import UmlPackageShape from '../../Types/UmlPackageShape';
import { ShapeGeoBuilder } from '@jxUml';

const PACKAGE_PADDING = 32;
const PACKAGE_HEADER = 40; // reserved space for the package title bar above its children
const MIN_PACKAGE_WIDTH = 320;
const PACKAGE_GAP = 60; // horizontal gap between packages, so a new one never overlaps an existing one

/**
 * First 3 dotted segments of a namespace = package name (rest is folder,
 * irrelevant for the shape). Fewer than 3 segments -> use all of them.
 * @param {string|null} namespace
 * @returns {string|null}
 */
export function derivePackageName(namespace) {
    if (!namespace) return null;
    const parts = namespace.split('.');
    return parts.slice(0, Math.min(3, parts.length)).join('.');
}

/**
 * @param {object[]} existingShapes
 * @param {string} packageName
 * @returns {UmlPackageShape|undefined}
 */
function findExistingPackage(existingShapes, packageName) {
    return existingShapes.find(function isMatchingPackage(shape) {
        return shape.kind === 'package' && shape.name === packageName;
    });
}

/**
 * Origin for a brand-new package: to the right of every existing top-level
 * package, never overlapping one — geometric overlap is what makes tryAdopt
 * (UmlCanvasEngine.addShape) wrongly adopt a new class into the WRONG,
 * already-on-canvas package. No existing packages -> falls back to the
 * caller's staggered x/y (fallbackX/fallbackY).
 * @param {object[]} existingShapes
 * @param {number} fallbackX
 * @param {number} fallbackY
 * @returns {{x: number, y: number}}
 */
function nextPackageOrigin(existingShapes, fallbackX, fallbackY) {
    const packages = existingShapes.filter(function isPackage(shape) {
        return shape.kind === 'package';
    });
    if (packages.length === 0) {
        return { x: fallbackX, y: fallbackY };
    }
    const maxRight = Math.max(...packages.map(function rightEdge(p) { return p.x + p.w; }));
    const topY = Math.min(...packages.map(function topEdge(p) { return p.y; }));
    return { x: maxRight + PACKAGE_GAP, y: topY };
}

/**
 * Resolves where the new shapes should be laid out: a brand-new package
 * (to the right of any existing packages), inside an already-on-canvas
 * package with a matching name (staggered below its existing children), or
 * plain fallback x/y when there's no namespace at all.
 * @param {object[]} existingShapes
 * @param {string|null} packageName
 * @param {number} x - fallback x when no package applies
 * @param {number} y - fallback y when no package applies
 * @returns {{originX: number, originY: number, packageOrigin: {x: number, y: number}|null, existingPackage: UmlPackageShape|undefined, buildNewPackage: boolean}}
 */
export function resolveOrigin(existingShapes, packageName, x, y) {
    const existingPackage = packageName ? findExistingPackage(existingShapes, packageName) : null;
    const buildNewPackage = Boolean(packageName) && !existingPackage;

    let originX = x;
    let originY = y;
    let packageOrigin = null;
    if (buildNewPackage) {
        packageOrigin = nextPackageOrigin(existingShapes, x, y);
        originX = packageOrigin.x + PACKAGE_PADDING;
        originY = packageOrigin.y + PACKAGE_HEADER;
    } else if (existingPackage) {
        // stagger new classes below whatever's already in the package
        const children = existingPackage.children || [];
        originX = existingPackage.x + PACKAGE_PADDING;
        originY = children.length > 0
            ? Math.max(...children.map(function bottomOf(c) { return c.y + ShapeGeoBuilder.effectiveHeight(c); })) + PACKAGE_PADDING
            : existingPackage.y + PACKAGE_HEADER;
    }
    return { originX, originY, packageOrigin, existingPackage, buildNewPackage };
}

/**
 * Builds a brand-new package shape around the just-built shapes, or grows an
 * already-on-canvas package in place so it keeps containing them (tryAdopt,
 * triggered per addShape call, relies on the package's bounds already fitting
 * its new children). No-op (returns null) when there are no shapes at all.
 * @param {object} params
 * @param {boolean} params.buildNewPackage
 * @param {UmlPackageShape|undefined} params.existingPackage
 * @param {{x: number, y: number}|null} params.packageOrigin
 * @param {string|null} params.packageName
 * @param {object[]} params.shapes - newly built class/interface shapes
 * @param {number} params.originX
 * @param {number} params.originY
 * @param {number} params.contentRight - right edge of the last built shape (no trailing gap)
 * @returns {UmlPackageShape|null}
 */
export function buildOrGrowPackageShape({ buildNewPackage, existingPackage, packageOrigin, packageName, shapes, originX, originY, contentRight }) {
    if (shapes.length === 0) return null;
    const maxShapeHeight = Math.max(...shapes.map(ShapeGeoBuilder.effectiveHeight));

    if (buildNewPackage) {
        const packageWidth = Math.max(MIN_PACKAGE_WIDTH, (contentRight - originX) + 2 * PACKAGE_PADDING);
        const packageHeight = PACKAGE_HEADER + maxShapeHeight + PACKAGE_PADDING;
        return new UmlPackageShape(uuid(), packageOrigin.x, packageOrigin.y, packageWidth, packageHeight, packageName);
    }
    if (existingPackage) {
        const neededWidth = (contentRight - existingPackage.x) + PACKAGE_PADDING;
        const neededHeight = (originY + maxShapeHeight - existingPackage.y) + PACKAGE_PADDING;
        existingPackage.w = Math.max(existingPackage.w, neededWidth);
        existingPackage.h = Math.max(existingPackage.h, neededHeight);
    }
    return null;
}
